import { Link } from "react-router-dom";
import { Mail, ArrowRight, Building2, GraduationCap, Factory, Home } from "lucide-react";

const verticals = [
  { label: "Healthcare", to: "/industries/healthcare", icon: Building2 },
  { label: "Education", to: "/industries/education", icon: GraduationCap },
  { label: "Manufacturing", to: "/industries/manufacturing", icon: Factory },
  { label: "Property Management", to: "/industries/property-management", icon: Home },
];

// Connects the Oracle specialization established in the hero to operational AP
// expertise — the real moat. Leads with the supplier-email differentiator
// (resolving AP work that happens before an invoice enters workflow), which is
// where EZ Cloud separates from invoice-OCR vendors.
const OperationalAP = () => {
  return (
    <section className="bg-gray-50 py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
            Where generic AP automation struggles
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-5 leading-tight">
            Built for Oracle AP environments where complexity is operational, not optional
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Complex approvals, decentralized purchasing, PO exceptions, supplier-email overload, and
            audit-sensitive workflows. EZ Cloud was engineered specifically for Oracle finance teams
            operating at enterprise scale — automating AP operations end-to-end, not just document capture.
          </p>
        </div>

        {/* Supplier-email differentiator */}
        <div className="max-w-4xl mx-auto bg-accent/10 border border-accent/30 rounded-2xl p-8 lg:p-10 mb-12">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-accent/20 rounded-xl flex items-center justify-center">
                <Mail className="w-7 h-7 text-accent" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                AP work starts before the invoice enters workflow
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Supplier-email management quietly consumes AP capacity — payment-status requests, missing
                PO numbers, duplicate submissions, banking-change requests, and endless follow-ups. EZ Cloud's
                AI resolves these supplier conversations automatically, with live Oracle context, before they
                ever reach your AP team.
              </p>
              <Link
                to="/technology/ai-email-prevention"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See how AI Email Prevention works
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Verticals */}
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-sm font-medium text-gray-500 mb-5">
            Proven in operationally complex AP environments
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {verticals.map(({ label, to, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-5 py-4 hover:border-accent/50 hover:shadow-sm transition-all"
              >
                <Icon className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                  {label}
                </span>
                <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>

          {/* Oracle environments — capability proof without customer logos */}
          <p className="text-center text-xs text-gray-400 mt-8">
            Oracle Fusion Cloud ERP · Oracle E-Business Suite · Oracle Integration Cloud · OCI-native deployment
          </p>
        </div>
      </div>
    </section>
  );
};

export default OperationalAP;
